import React from 'react';
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import AppDialog from '../common/AppDialog';
import {
  Person as PersonIcon,
} from '@mui/icons-material';
import { Customer } from '../../types';
import { useLanguage } from '../../contexts/LanguageContext';

interface DeleteCustomerDialogProps {
  open: boolean;
  customer: Customer | null;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  isLoading?: boolean;
}

const DeleteCustomerDialog: React.FC<DeleteCustomerDialogProps> = ({
  open,
  customer,
  onClose,
  onConfirm,
  isLoading = false,
}) => {
  const { t } = useLanguage();
  
  const handleConfirm = async () => {
    await onConfirm();
    onClose();
  };

  if (!customer) return null;

  return (
    <AppDialog presentation="confirm"
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>{t('customers.deleteCustomer')}</DialogTitle>

      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2">
            {t('customers.deleteWarning')}
          </Typography>
        </Alert>

        <Box display="flex" alignItems="center" gap={2} p={2} bgcolor={(theme) => theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50'} borderRadius={1}>
          <PersonIcon color="action" />
          <Box>
            <Typography variant="subtitle1" fontWeight="medium">
              {customer.firstName} {customer.lastName}
            </Typography>
            {customer.email && (
              <Typography variant="body2" color="text.secondary">
                {customer.email}
              </Typography>
            )}
          </Box>
        </Box>

        <Typography variant="body1" sx={{ mt: 2 }}>
          {t('customers.deleteQuestion')}
        </Typography>
        
        <Box component="ul" sx={{ mt: 1, mb: 0 }}>
          <li>{t('customers.deleteConsequences.allTransactions')}</li>
          <li>{t('customers.deleteConsequences.allInvoices')}</li>
          <li>{t('customers.deleteConsequences.allData')}</li>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={onClose}
          disabled={isLoading}
          variant="outlined"
        >
          {t('common.cancel')}
        </Button>
        <Button
          onClick={handleConfirm}
          data-testid="customer-delete-confirm"
          disabled={isLoading}
          variant="contained"
          color="error"
          startIcon={isLoading ? <CircularProgress size={20} /> : null}
        >
          {isLoading ? t('customers.deleting') : t('customers.deleteCustomer')}
        </Button>
      </DialogActions>
    </AppDialog>
  );
};

export default DeleteCustomerDialog;
